import { ModelRow, StringUtil } from "@mcleod/core";
import { BlurEvent, Image, ImageProps, Label, LookupModelSelectionEvent, Panel, Snackbar, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { getDispatchControlBoolean, getDispatchControlValue } from "@mcleod/general";
import { EquipmentGroupValidation } from "./EquipmentGroupValidation";
import { EquipmentGroupWarnings } from "./EquipmentGroupWarnings";
import { UnassignDialog } from "../UnassignDialog";
import { AutogenLayoutBatchOrderRouting } from "./autogen/AutogenLayoutBatchOrderRouting";
import { ImageName } from "@mcleod/images";

export class BatchOrderRouting extends AutogenLayoutBatchOrderRouting {
    private _equipmentGroupId: string | null = null;
    private trailerContinuity: boolean = getDispatchControlBoolean("trailer_continuity", false);
    private _equipmentValidationErrors: string[] = [];

    get equipmentValidationErrors(): string[] {
        return this._equipmentValidationErrors;
    }

    set equipmentValidationErrors(errors: string[]) {
        this._equipmentValidationErrors = errors;
    }

    get equipmentGroupId(): string | null {
        return this._equipmentGroupId;
    }

    set equipmentGroupId(equipmentGroupId: string | null) {
        this._equipmentGroupId = equipmentGroupId;
    }

    /** This is an event handler for the onRowDisplay event of tableBatchOrderRouting. */
    tableBatchOrderRoutingOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const maximumDrivers = getDispatchControlValue("maximum_drivers");
        const maximumTrailers = getDispatchControlValue("maximum_trailers");
        const typeSequence = parseInt(modelRow.get("type_sequence"));
        const panelTractorInfo = tableRow.findComponentById("panelTractorInfo") as Panel;
        if (typeSequence != 0) panelTractorInfo.visible = false;

        const textboxDriverId = tableRow.findComponentById("textboxDriverId") as Textbox;
        textboxDriverId.caption = `Driver ${typeSequence == 0 ? "1" : "2"} Name`;
        textboxDriverId.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'Y');
        if (typeSequence === 0) textboxDriverId.required = true;
        textboxDriverId.quickInfoLayout = "lme/dispatch/asset/DriverQuickInfo";
        textboxDriverId.quickInfoOverrideKeyValue = modelRow.get("driver_id");

        const textboxDollyNumber = tableRow.findComponentById("textboxDollyNumber") as Textbox;
        const textboxTrailerNumber = tableRow.findComponentById("textboxTrailerNumber") as Textbox;
        textboxTrailerNumber.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        const panelTrailerDolly = tableRow.findComponentById("panelTrailerDolly") as Panel;
        const panelDriver = tableRow.findComponentById("panelDriver") as Panel;
        if (typeSequence >= maximumDrivers) {
            textboxDriverId.visible = false;
            panelDriver.minWidth = Number(panelDriver.width);
        }

        if (typeSequence >= maximumTrailers) {
            panelTrailerDolly.visible = false;
        } else {
            const dollyLabels = ["1", "2"];
            textboxDollyNumber.caption = `Dolly ${dollyLabels[typeSequence - 1] || (typeSequence).toString()}`;
            textboxDollyNumber.quickInfoLayout = "lme/dispatch/asset/DollyQuickInfo";
            textboxDollyNumber.quickInfoOverrideKeyValue = modelRow.get("dolly_id");
            if (typeSequence === 0) {
                textboxDollyNumber.visible = false;
            }

            const trailerLabels = ["1", "2", "3"];
            textboxTrailerNumber.caption = `Trailer ${trailerLabels[typeSequence] || (typeSequence + 1).toString()}`;
            if (typeSequence === 0 && this.trailerContinuity) textboxTrailerNumber.required = true;
            textboxTrailerNumber.quickInfoLayout = "lme/dispatch/asset/TrailerQuickInfo";
            textboxTrailerNumber.quickInfoOverrideKeyValue = modelRow.get("trailer_id");
        }

        const textboxTractorNumber = tableRow.findComponentById("textboxTractorNumber") as Textbox;
        textboxTractorNumber.addBeforeLookupModelSearchListener((event) => event.filter.service_status = 'A');
        textboxTractorNumber.required = (typeSequence === 0);
        textboxTractorNumber.tooltip = null;
        textboxTractorNumber.quickInfoLayout = "lme/dispatch/asset/TractorQuickInfo";
        textboxTractorNumber.quickInfoOverrideKeyValue = modelRow.get("tractor_id");
    }

    /** This is an event handler for the beforeRowSave event of tableBatchOrderRouting. */
    tableBatchOrderRoutingBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        modelRow._appending = true; // For some reason, we create new equipment item records each we make an update so it'll always be appending
    }

    async onEquipmentSelect(event: LookupModelSelectionEvent) {
        if (event.hasSelection() !== true)
            return;
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox) as TableRow;
        const modelRow = tableRow.data;

        // Collect individual trailer IDs from the equipment group
        const maximumTrailers = getDispatchControlValue("maximum_trailers");
        let trailer2Id: string | null = null;
        let trailer3Id: string | null = null;

        if (this.sourceEquipmentGroup.data.length > 1 && maximumTrailers >= 2) {
            trailer2Id = this.sourceEquipmentGroup.data[1].get("trailer_id", null);
        }
        if (this.sourceEquipmentGroup.data.length > 2 && maximumTrailers >= 3) {
            trailer3Id = this.sourceEquipmentGroup.data[2].get("trailer_id", null);
        }

        const validationResult = await EquipmentGroupValidation.validate({
            checkInProgress: true,
            preassignment: true,
            movementId: null,
            modelRow, field: textbox.field,

            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            ...(this.sourceEquipmentGroup.data.length > 1 ?
                { driver2Id: this.sourceEquipmentGroup.data[1].get("driver_id", null) }
                : {}),
            trailerId: this.sourceEquipmentGroup.data[0].get("trailer_id", null),
            trailer2Id: trailer2Id,
            trailer3Id: trailer3Id,
            batchOrderMode: true
        });

        const unassignDialogSuccess = (validationResult.errors.length == 0)
            ? await this.checkForUnassigned(textbox.field, modelRow)
            : true;

        if (unassignDialogSuccess) {
            if (this.isEquipmentWarningPresent(validationResult.driverRegulatoryIssues)) {
                const driverPanel = new Panel();
                const regulatoryImageProps: Partial<ImageProps> = {
                    id: "imageEquipWarning",
                    name: ImageName.chatLines,
                    color: "primary.light",
                    height: 26,
                    width: 22,
                    marginLeft: 8,
                    rowBreak: false
                };
                const regulatoryWarningImage = new Image(regulatoryImageProps);
                const label = new Label({
                    text: "Regulatory Requirements",
                    fontBold: true, rowBreak: true,
                    color: "primary.light"
                });
                driverPanel.add(regulatoryWarningImage, label);
                for (const driver in validationResult.driverRegulatoryIssues) {
                    const driverRegulatoryIssues = validationResult.driverRegulatoryIssues[driver];
                    const hasWarnings = (driverRegulatoryIssues.warnings.length > 0);
                    const hasErrors = (driverRegulatoryIssues.errors.length > 0);
                    if (hasWarnings || hasErrors) {
                        EquipmentGroupWarnings.populateRegulatoryIssuePanel(driverPanel, driverRegulatoryIssues);
                    }
                }
                Snackbar.showSnackbar(driverPanel, { customBackground: "subtle.lightest", customMaxWidth: 900, millisUntilDismissal: 15000 });
            }
        }
    }

    private isEquipmentWarningPresent(issues: any): boolean {
        if (!issues) {
            return false;
        } else if (issues.driver_id?.warnings?.length > 0 || issues.driver_id?.errors?.length > 0 ||
            issues.driver_two_id?.warnings?.length > 0 || issues.driver_two_id?.errors?.length > 0) {
            return true;
        }
    }

    calculateRegulatoryIssuePanelHeight(items: any[], rowLength: number, lineHeight: number, blankSpace: number): number {
        let calcHeight = 0;
        calcHeight += blankSpace;

        items?.forEach(item => {
            calcHeight += (((Math.floor(item.length / rowLength)) + 1) * lineHeight);
        }
        );
        return calcHeight;
    }

    async equipmentOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox) as TableRow;
        if (textbox.isDropdownVisible() || textbox.validationWarning != null) return;
        const modelRow = textbox.getRelevantModelRow();
        if (StringUtil.isEmptyString(modelRow.get(textbox.field))) {
            await this.checkForUnassigned(textbox.field, modelRow);
        }
    }

    async checkForUnassigned(field: string, modelRow: ModelRow) {
        if (modelRow.originalData
            && !StringUtil.isEmptyString(modelRow.originalData[field])
            && !StringUtil.isEmptyString(modelRow.data[field])
            && modelRow.originalData[field] != modelRow.data[field]
            && getDispatchControlBoolean("req_unassign_reason", false)) {
            const resultRow = await new UnassignDialog("B").show();
            if (resultRow) {
                this.activeRow.set("unassign_reason", resultRow);
                if (field === "tractor_id" && StringUtil.isEmptyString(modelRow.get(field))) { // clear driver as well
                    this.activeRow.set("driver_id", null);
                }
            } else {
                modelRow.revertToOriginalData(field);
                if (field === "tractor_id") {
                    modelRow.revertToOriginalData("driver_id");
                    modelRow.revertToOriginalData("driver_two_id");
                }
                return false;
            }
        }
        return true;
    }

    async validateLayout(): Promise<string | null> {
        const modelRow: ModelRow = this.tableBatchOrderRouting?.data[0];

        if (modelRow === null || modelRow === undefined) {
            return null;
        }

        const tractorID = modelRow.get("tractor_id", null);
        const driverId = modelRow.get("driver_id", null);
        const trailerId = modelRow.get("trailer_id", null);
        const isTractorDriverEmpty = StringUtil.isEmptyString(tractorID) || StringUtil.isEmptyString(driverId);

        if (this.trailerContinuity && (isTractorDriverEmpty || StringUtil.isEmptyString(trailerId))) {
            return "Tractor, Driver, and Trailer are required in order to Save the Equipment Group.";
        }

        if (isTractorDriverEmpty) {
            return "Tractor and Driver are required in order to Save the Equipment Group.";
        }

        this.equipmentValidationErrors = [];
        const validationResult = await EquipmentGroupValidation.validate({
            checkInProgress: true,
            preassignment: true,
            movementId: null,
            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            ...(this.sourceEquipmentGroup.data.length > 1 ?
                { driver2Id: this.sourceEquipmentGroup.data[1].get("driver_id", null) }
                : {}),
            trailerId: this.sourceEquipmentGroup.data[0].get("trailer_id", null),
            batchOrderMode: true
        });

        if (validationResult.errors && validationResult.errors.length > 0) {
            this.equipmentValidationErrors.push(...validationResult.errors.map((e: any) => e.message));
            return this.equipmentValidationErrors.join("\n");
        }
        return null;
    }
}
